import _ from 'underscore';
import { CampaignTypes, biddingStrategyTypeId } from '@bingads-webui-campaign-react/constants';
import { parseViewModelToMTModelForPMax, parseViewModelToMTModelForSearch, parseViewModelToMTModelForShopping } from './api-parser';

export const API_VERSION = {
  V1: '1.0',
  V2: '2.0',
};

export const MODE = {
  CREATION: 'CREATION',
  EDITING: 'EDITING',
};

export const BudgetSuggestionTiers = {
  Advanced: 'Advanced',
  Balanced: 'Balanced',
  Starter: 'Starter',
};

export const CustomBudget = 'CustomBudget';

export const BudgetSuggestionOfflineTiers = {
  Suggested: 'Suggested',
  Current: 'Current',
};

export const RecommendedBudgetSuggestionTier = 'Suggested';

export const ApiKeysBudgetSuggestion = {
  Advanced: 'High',
  Balanced: 'Mid',
  Starter: 'Low',
};

export const BudgetTierTitles = {
  Advanced: _TL_('Growth'),
  Balanced: _TL_('Recommended'),
  Starter: _TL_('Basic'),
  Suggested: _TL_('Recommended'),
  Current: _TL_('Current budget'),
};

export const BudgetTypesTooltip = _TL_('These are budget levels based on what works for similar businesses. Basic spends the least and reaches the fewest people, which is useful for testing. Recommended balances spending and results. Growth is the highest suggested level, for reaching the most people your budget can cover. None of these change how quickly your budget is spent during the day.');

export const BudgetDetailKeys = {
  Impressions: 'Impressions',
  Clicks: 'Clicks',
  CTR: 'CTR',
  CostPerClick: 'CostPerClick',
  Conversions: 'Conversions',
  CostPerConversion: 'CostPerConversion',
};

export const BudgetDetailKeysByBidType = {
  [biddingStrategyTypeId.MaxConversions]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.Conversions,
    BudgetDetailKeys.CostPerConversion,
  ],
  [biddingStrategyTypeId.MaxConversionValue]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.Conversions,
    BudgetDetailKeys.CostPerConversion,
  ],
  [biddingStrategyTypeId.MaxClicks]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.CTR,
    BudgetDetailKeys.CostPerClick,
  ],
  [biddingStrategyTypeId.TargetImpressionShare]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.CTR,
    BudgetDetailKeys.CostPerClick,
  ],
  [biddingStrategyTypeId.TargetCpa]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.Conversions,
    BudgetDetailKeys.CostPerConversion,
  ],
  [biddingStrategyTypeId.TargetRoas]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.Conversions,
    BudgetDetailKeys.CostPerConversion,
  ],
  [biddingStrategyTypeId.EnhancedCpc]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.CTR,
    BudgetDetailKeys.CostPerClick,
  ],
  [biddingStrategyTypeId.CostPerSale]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.CTR,
    BudgetDetailKeys.CostPerClick,
  ],
  Default: [],
};


export const BudgetDetailKeysByCampaignType = {
  [CampaignTypes.PerformanceMax]: [
    BudgetDetailKeys.Clicks,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.Conversions,
    BudgetDetailKeys.CostPerConversion,
  ],
  Default: [
    BudgetDetailKeys.Conversions,
    BudgetDetailKeys.CTR,
    BudgetDetailKeys.Impressions,
    BudgetDetailKeys.Clicks,
  ],
};

export const BudgetDetailsKeysText = {
  [BudgetDetailKeys.Impressions]: {
    title: _TL_('Monthly impr.'),
    popupId: 'pop_BA_MonthlyImpr',
    helpPopupTitle: 'Monthly Impressions',
  },
  [BudgetDetailKeys.Clicks]: {
    title: _TL_('Monthly clicks'),
    popupId: 'pop_BA_MonthlyClicks',
    helpPopupTitle: 'Monthly clicks',
  },
  [BudgetDetailKeys.CTR]: {
    title: _TL_('Avg. CTR'),
    popupId: 'pop_BA_AverageCTR',
    helpPopupTitle: 'Average Click through rate',
  },
  [BudgetDetailKeys.CostPerClick]: {
    title: _TL_('Avg. CPC'),
    popupId: 'pop_BA_AverageCostPerClick',
    helpPopupTitle: 'Average CPC',
  },
  [BudgetDetailKeys.Conversions]: {
    title: _TL_('Monthly conv.'),
    popupId: 'pop_BA_MonthlyConv',
    helpPopupTitle: 'Monthly conversions',
  },
  [BudgetDetailKeys.CostPerConversion]: {
    title: _TL_('Avg. CPA'),
    popupId: 'pop_BA_AverageCostPerAcquisition',
    helpPopupTitle: 'Average CPA',
  },
};


export const getMatchingBudgetTierBasedOnBudget = (budgetSuggestionData, budgetAmount, mode) => {
  if (!budgetAmount) {
    return mode === MODE.CREATION ? BudgetSuggestionTiers.Balanced : BudgetSuggestionOfflineTiers.Suggested;
  }

  let matchingBudgetTier = mode === MODE.CREATION ? CustomBudget : BudgetSuggestionOfflineTiers.Suggested;

  if (mode === MODE.CREATION) {
    _.each(budgetSuggestionData, (budget, key) => {
      if (budget.BudgetAmountDaily === parseFloat(budgetAmount)) {
        matchingBudgetTier = key;
      }
    });
  } else {
    _.each(budgetSuggestionData, (budget, key) => {
      if (budget.BudgetAmountMonthly === parseFloat(budgetAmount)) {
        matchingBudgetTier = key;
      }
    });
  }

  return matchingBudgetTier;
};

const parserByCampaignType = {
  [CampaignTypes.PerformanceMax]: parseViewModelToMTModelForPMax,
  [CampaignTypes.Shopping]: parseViewModelToMTModelForShopping,
  Default: parseViewModelToMTModelForSearch,
};


export const getParserByCampaignType = campaignType => parserByCampaignType[campaignType] || parserByCampaignType.Default;

const singleDecimalOptions = {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
};

export const formatNumber = (num, i18n) => {
  if (num === undefined || num === null || num === '') {
    return '';
  }

  const value = Number(num);
  if (!Number.isFinite(value)) {
    return '';
  }

  const formatSingleDecimal = scaledValue => (
    i18n
      ? i18n.formatDecimal(scaledValue, singleDecimalOptions)
      : scaledValue.toFixed(1)
  );

  if (value >= 1e9) {
    return `${formatSingleDecimal(value / 1e9)}B`;
  } else if (value >= 1e6) {
    return `${formatSingleDecimal(value / 1e6)}M`;
  } else if (value >= 1e3) {
    return `${formatSingleDecimal(value / 1e3)}K`;
  }

  return i18n ? i18n.formatDecimal(value) : value.toString();
};

export const CampaignTypeToAdvertiserChannelTypeId = {
  [CampaignTypes.Default]: 1,
  [CampaignTypes.Product]: 2,
  [CampaignTypes.Shopping]: 3,
  [CampaignTypes.DynamicSearchAds]: 4,
  [CampaignTypes.Hotel]: 5,
  [CampaignTypes.Audience]: 6,
  [CampaignTypes.Smart]: 7,
  [CampaignTypes.Hotel]: 8,
  [CampaignTypes.PerformanceMax]: 9,
};
